// src/utils/sectionKind.js
/**
 * Heuristic normalizer that returns a stable "kind" for a section, e.g.:
 *   - "product.triggers"
 *   - "product.previousSolutions"
 *   - "product.consideredSolutions"
 *   - "product.impressions"
 *   - "product.likesDislikes"
 *   - "product.situations"
 *   - "product.winVsStruggle"
 *   - "job.importanceSatisfaction"
 *   - "job.triggers"
 *   - "job.goodBad"
 *   - "job.situations"
 *   - "job.desiredOutcomes"
 *   - "job.measures"
 */
export function getSectionKind(section = {}, mode = 'product') {
    // 1) If backend already provides a key/id/slug, prefer it.
    const metaKey =
      section.key ||
      section.id ||
      section.slug ||
      section.metaKey ||
      section.metaTitle?.key ||
      null;
    if (metaKey) return String(metaKey);
  
    // 2) Infer from data shape / tile types (strong signals)
    const tiles = section.tiles || [];
    const tileTypes = new Set(tiles.map(t => t?.type).filter(Boolean));
    const hasProductForJob = [...tileTypes].includes('productForJob');
    const hasTrigger = [...tileTypes].includes('trigger');
    const hasMeasure = [...tileTypes].includes('measure');
    const looksLikeJobMap = section.type === 'jobMapBoard';
  
    if (looksLikeJobMap) return 'job.map';
    if (mode === 'job' && hasProductForJob) return 'job.importanceSatisfaction';
    if (mode === 'product' && hasProductForJob) return 'product.winVsStruggle';
    if (hasTrigger) return mode === 'product' ? 'product.triggers' : 'job.triggers';
    if (hasMeasure) return 'job.measures';
  
    // 3) Weak signals: title-based fallback (kept as last resort; you can delete later)
    const t = String(section.title || '').toLowerCase();
  
    const any = (re) => re.test(t);
    if (mode === 'product') {
      if (any(/consider(ed)?\s+solutions/)) return 'product.consideredSolutions';
      if (any(/previous\s+solutions|switch\s+away/)) return 'product.previousSolutions';
      if (any(/overall\s+impressions/)) return 'product.impressions';
      if (any(/likes?\s*and\s*dislikes?/)) return 'product.likesDislikes';
      if (any(/situations.*(continue|stop).*product/)) return 'product.situations';
      if (any(/where.*(win|struggle)/)) return 'product.winVsStruggle';
      if (any(/triggers/)) return 'product.triggers';
    } else {
      if (any(/importance.*satisfaction/)) return 'job.importanceSatisfaction';
      if (any(/good.*&.*bad|good.*bad/)) return 'job.goodBad';
      if (any(/measure\s+success/)) return 'job.measures';
      if (any(/situations/)) return 'job.situations';
      if (any(/desired\s+outcomes/)) return 'job.desiredOutcomes';
      if (any(/triggers/)) return 'job.triggers';
    }
  
    // 4) Unknown fallback (safe default)
    return `${mode}.unknown`;
  }
  